import { DsChipColorVariant, DsColors, Icons, IconStyles, TableChipConfig } from '@levelaccess/design-system';
import { $severity } from '../../../shared/constants/accessibility';

export class SeverityChipUtility {
  private static readonly customRankIcons: Icons[] = [Icons.ChevronUp, Icons.ChevronDown];

  public static getChipVariant(severity: string): DsChipColorVariant {
    switch (severity) {
      case $severity.critical:
      case $severity.high:
      case $severity.low:
      case $severity.custom1:
      case $severity.custom2:
        return DsChipColorVariant.Light;
      case $severity.info:
      default:
        return DsChipColorVariant.Primary;
    }
  }

  public static getIconColor(severity: string, options?: { defaultColor?: keyof typeof DsColors }): keyof typeof DsColors {
    switch (severity) {
      case $severity.critical:
        return 'SeverityCritical';
      case $severity.high:
        return 'SeverityMedium';
      case $severity.low:
        return 'SeverityLow';
      case $severity.custom1:
        return 'SeverityMedium';
      case $severity.custom2:
        return 'Teal';
      default:
        return options?.defaultColor ?? 'Primary';
    }
  }

  public static getIcon(
    severity: string,
    options?: {
      icon?: Icons;
      customSeverityKeysSortedByRank?: $severity[];
    },
  ): Icons {
    if (options?.icon) {
      return options.icon;
    }

    switch (severity) {
      case $severity.critical:
        return Icons.CircleExclamation;
      case $severity.high:
        return Icons.ChevronsUp;
      case $severity.low:
        return Icons.ChevronsDown;
      case $severity.custom1:
      case $severity.custom2: {
        if (options?.customSeverityKeysSortedByRank?.includes(severity as $severity)) {
          const customIndex: number = options.customSeverityKeysSortedByRank.indexOf(severity as $severity);
          const customIcon: Icons | undefined = this.customRankIcons[customIndex];
          if (customIcon) {
            return customIcon;
          }
        }
        break;
      }
      default:
        break;
    }

    return Icons.CircleExclamation;
  }

  public static getTableChipConfig(
    severity: string,
    text: string,
    options?: {
      defaultColor?: keyof typeof DsColors;
      customSeverityKeysSortedByRank?: $severity[];
    },
  ): TableChipConfig {
    const colorVariant: DsChipColorVariant = this.getChipVariant(severity);
    const config: TableChipConfig = {
      text,
      colorVariant,
      showIcon: true,
      iconName: this.getTableIcon(severity, options),
      iconStyle: IconStyles.Regular,
    };

    if (colorVariant === DsChipColorVariant.Light) {
      config.iconColor = DsColors[this.getTableIconColor(severity, options)];
    }

    return config;
  }

  private static getTableIcon(
    severity: string,
    options?: {
      icon?: Icons;
      customSeverityKeysSortedByRank?: $severity[];
    },
  ): Icons {
    return this.getIcon(severity, {
      ...options,
      customSeverityKeysSortedByRank: options?.customSeverityKeysSortedByRank ?? [$severity.custom2, $severity.custom1],
    });
  }

  private static getTableIconColor(severity: string, options?: { defaultColor?: keyof typeof DsColors }): keyof typeof DsColors {
    switch (severity) {
      case $severity.critical:
        return 'SeverityCritical';
      case $severity.high:
      case $severity.custom2:
        return 'SeverityMedium';
      case $severity.custom1:
        return 'Info';
      case $severity.low:
        return 'SeverityLow';
      default:
        return this.getIconColor(severity, options);
    }
  }
}
